import { getUuid } from '@front10/helpers/dist/uuid';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import getStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import Popover from '../Popover';
import Box from '../../elements/Box';
import Text from '../../elements/Text';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import Input from '../../elements/Input';
import DayPicker from './DayPicker';
import { weekDaysLong, weekDaysShort } from '../../../helpers/localization/localizationUtils';
import { getDateAriaLabel } from '../../items/_commons/atomsHelpers';
import { isEmpty, isNilOrEmpty, replaceAll, removeInvalidCharacters } from '../../../helpers/utils';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import LocationSelectorFilter from '../LocationSelector';

import Checkbox from '../../elements/Checkbox';

import {
  blur,
  focus,
  isDate,
  isDayAfterOrSame,
  isDayBefore,
  isDayInRange,
  isFutureOrPresentDay,
  isSameDay,
  isValidRange,
  parseDate,
  searchSeparator,
  isDayAfter
} from '../../../helpers/date';
import { formatDate } from './commons';

const KEY_BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_PAGE_UP = 33;
const KEY_PAGE_DOWN = 34;
const KEY_ARROW_LEFT = 37;
const KEY_ARROW_UP = 38;
const KEY_ARROW_RIGHT = 39;
const KEY_ARROW_DOWN = 40;
const NOT_AVALIABLES_DAYS = -1;

const DATE_OUT_OF_RANGE = 0;
const INCORRECT_FORMAT = 1;

//Finit State machine used to model behavior of date selection
const STATES = {
  START: 'START',
  INTER: 'INTER',
  END: 'END'
};
// Making inmutable the states
Object.freeze(STATES);

const machine = {
  [STATES.START]: {
    stay: STATES.START,
    selectTo: STATES.INTER
  },
  [STATES.INTER]: {
    selectFrom: STATES.START
  },
  [STATES.END]: {
    selectFrom: STATES.START,
    selectTo: STATES.INTER
  }
};
// Making inmutable the machine variable
Object.freeze(machine);

class DatesRangeSelector extends React.Component {
  state = {
    from: this.props.defaultValue.start,
    to: this.props.defaultValue.end,
    fromInput:
      this.props.defaultValue.start &&
      formatDate(this.props.defaultValue.start, this.props.format, this.context),
    toInput:
      this.props.defaultValue.end &&
      formatDate(this.props.defaultValue.end, this.props.format, this.context),
    startPoint: undefined,
    endPoint: undefined,
    possibleInitialRange: undefined,
    isFirstInputClicked: false,
    showCloseIcon: !!(this.props.defaultValue.start || this.props.defaultValue.end),
    currentRightIcon: this.props.rightIcon,
    valid: undefined,
    machineState: STATES.START,
    lastMachineState: undefined,
    statesHistory: [],
    isCheckedFlexibleDates: this.props.enableFlexibleDates && this.props.checkedFlexibleDates,
    flexibleDatesValues: this.props.flexibleDatesRangeValues.defaultValue,
    showAccessibiltyMessage: undefined,
    typeError: undefined
  };

  handleFlexibleDatesChange = (value) => {
    this.setState({
      flexibleDatesValues: value
    });
  };

  handleIsCheckedFlexibleDatesChange = (value) => {
    this.setState({
      isCheckedFlexibleDates: value
    });
    this.dayPickerRef.current.focus();
  };
  currentDate = dayjs().toDate();

  modifiers = {
    today: (day) => isSameDay(day, dayjs().toDate()),
    beginRange: (day) => {
      return isSameDay(day, this.state.startPoint); // || isSameDay(day, this.state.from);
    },
    endRange: (day) => isSameDay(day, this.state.endPoint),
    selected: (day) =>
      isDayInRange(day, {
        from: this.state.from,
        to: this.state.to
      }),
    past: (day) => {
      if (this.props.departure) {
        return (
          isDayBefore(day, dayjs().add(Number(this.props.departure.start), 'day').toDate()) ||
          isDayAfter(
            day,
            dayjs()
              .add(Number(this.props.departure.start) + Number(this.props.departure.end) - 1, 'day')
              .toDate()
          )
        );
      } else {
        return (
          isDayBefore(day, dayjs().toDate()) ||
          (this.props.availableDays &&
            !(
              isSameDay(day, dayjs().add(this.props.availableDays, 'day').toDate()) ||
              isDayBefore(day, dayjs().add(this.props.availableDays, 'day').toDate())
            ))
        );
      }
    },
    disabled: {
      before:
        this.props.departure && Number(this.props.departure.start)
          ? dayjs().add(Number(this.props.departure.start), 'day').toDate()
          : this.currentDate,
      after:
        this.props.departure &&
        Number(this.props.departure.end) >= 0 &&
        Number(this.props.departure.start) >= 0
          ? dayjs()
              .add(Number(this.props.departure.end) + Number(this.props.departure.start) - 1, 'day')
              .toDate()
          : dayjs()
              .add(this.props.availableDays - 1, 'day')
              .toDate()
    }
  };

  id = getUuid();

  separator = '';

  deBounce = null;

  focusElement = '';

  constructor(props) {
    super(props);
    props.focusReferences(this);
    const { format } = this.props;
    this.separator = searchSeparator(format);
    this.fromRef = React.createRef();
    this.toRef = React.createRef();
    this.popupRef = React.createRef();
    this.popoverContainerRef = React.createRef();
    this.inputRef = React.createRef();
    this.target = React.createRef();
    this.dayPickerRef = React.createRef();
    this.weekdaysLong = [];
    this.weekdaysShort = [];
  }

  componentDidMount() {
    const { translate, localizationData } = this.context;
    this.weekdaysLong = weekDaysLong(translate);
    this.weekdaysShort = weekDaysShort(translate, localizationData);
  }

  throwOnChangeEvent = ({ flexibleDatesValues, newfrom } = {}) => {
    const {
      toInput,
      fromInput,
      to,
      from,
      flexibleDatesValues: flexibleValues,
      isCheckedFlexibleDates
    } = this.state;

    const flexibleDates = isCheckedFlexibleDates ? flexibleDatesValues || flexibleValues : 0;

    this.props.onChange({
      start: newfrom ? newfrom : fromInput ? from : undefined,
      end: toInput ? to : undefined,
      flexibleDates: flexibleDates,
      availableDays:
        this.props.departure &&
        Number(this.props.departure.end) >= 0 &&
        Number(this.props.departure.start) >= 0
          ? Number(this.props.departure.end) + Number(this.props.departure.start)
          : this.props.availableDays || NOT_AVALIABLES_DAYS,
      enabledFlexibleDates: this.props.enableFlexibleDates,
      checkedFlexibleDates: isCheckedFlexibleDates
    });
  };
  update = () => {
    const { inputDateScreenReader } = this.props;
    const { from, to } = this.state;
    const { translate, localizationData } = this.context;

    if (this.isValid(from, to)) {
      // TODO: For accessibility in inputs
      const message = `${translate('cmp-label--datepicker-depart', 'Depart')} ${translate(
        'cmp-preposition--origin-place',
        'From'
      )} ${getDateAriaLabel(from, translate, inputDateScreenReader, localizationData)}, ${translate(
        'cmp-label--datepicker-return',
        'Return'
      )} ${translate('cmp-preposition--destination-place', 'To')} ${getDateAriaLabel(
        to,
        translate,
        inputDateScreenReader,
        localizationData
      )}`;
      this.manageAriaLabel(message, message);
    }
  };

  updateState = (params, inputFocus = null, shouldUpdate = false, fn, event = undefined) => {
    return new Promise((resolve) => {
      const callBack = fn && typeof fn === 'function' ? fn : () => {};
      this.setState({ ...params }, () => {
        if (inputFocus) {
          this.goToNextInput(inputFocus);
        }
        if (shouldUpdate) {
          this.update(event);
        }
        resolve(callBack());
      });
    });
  };

  handleKeyboardChange = (date, target) => {
    const { to, from, toInput } = this.state;
    const { format, inputDateScreenReader } = this.props;
    const { translate, localizationData } = this.context;
    let fromValue;
    let toValue;
    if (isFutureOrPresentDay(date)) {
      if (
        (target === 'from' && isDayAfter(date, to)) ||
        (target === 'to' && isDayBefore(date, from))
      ) {
        fromValue = date;
        toValue = date;
      } else if (target === 'from') {
        fromValue = date;
      } else {
        fromValue = !from ? date : from;
        toValue = date;
      }

      // TODO: For accessibility in inputs
      const message = `${translate('cmp-label--datepicker-depart', 'Depart')} ${translate(
        'cmp-preposition--origin-place',
        'From'
      )} ${getDateAriaLabel(
        fromValue,
        translate,
        inputDateScreenReader,
        localizationData
      )}, ${translate('cmp-label--datepicker-return', 'Return')} ${translate(
        'cmp-preposition--destination-place',
        'To'
      )} ${getDateAriaLabel(toValue, translate, inputDateScreenReader, localizationData)}`;
      this.manageAriaLabel(message, message);

      this.updateState(
        {
          from: fromValue,
          fromInput: formatDate(fromValue, format, this.context),
          to: toValue || to,
          toInput: toValue ? formatDate(toValue, format, this.context) : toInput,
          startPoint: fromValue,
          endPoint: toValue,
          possibleInitialRange: undefined,
          showCloseIcon: true
        },
        null,
        false,
        this.updateValidState
      );
    }
  };

  manageAriaLabel = (from, to) => {
    if (this.fromRef && this.fromRef.current && this.toRef && this.toRef.current) {
      setTimeout(() => {
        if (this.fromRef && this.fromRef.current && this.toRef && this.toRef.current) {
          this.fromRef.current.setAttribute('aria-label', from);
          this.toRef.current.setAttribute('aria-label', to);
        }
      }, 200);
    }
  };

  send = (transition, payload = {}) => {
    const { machineState, statesHistory } = this.state;
    const currentState = machine[machineState];
    const nextState = currentState[transition];
    if (nextState) {
      this._dispatch(transition, payload);
      const newStatesHistory = [...statesHistory, machineState];
      this.setState({
        machineState: nextState,
        statesHistory: newStatesHistory,
        lastMachineState: machineState
      });
    }
    // console.log('current state: ', machineState, '-->', transition, '-->', nextState);
  };

  _dispatch = (transition, payload) => {
    switch (transition) {
      case 'selectFrom':
        this.onSelectFrom(payload);
        break;
      case 'stay':
        this.stay(payload);
        break;
      case 'selectTo':
        this.onSelectTo(payload);
        break;
      default:
        break;
    }
  };

  onSelectFrom = ({ date, clearTo = false, sameDate = false, close = false }) => {
    const { to, toInput } = this.state;
    const { format } = this.props;
    const toValue = sameDate ? date : to;
    this.updateState(
      {
        from: date,
        fromInput: formatDate(date, format, this.context),
        to: clearTo ? undefined : toValue,
        toInput: clearTo
          ? undefined
          : toValue
          ? formatDate(toValue, format, this.context)
          : toInput,
        startPoint: date,
        ...(clearTo ? { endPoint: undefined } : {}),
        possibleInitialRange: date,
        showCloseIcon: true
      },
      'to',
      true,
      () => {
        this.updateValidState();
        if (!this.props.enableFlexibleDates && close) {
          this.throwOnChangeEvent();
          this.popover.closePopover();
        }
      }
    );
  };

  onSelectTo = ({ date, sameDate = false, close = true }) => {
    const { from } = this.state;
    const { format } = this.props;
    const fromValue = !from || sameDate ? date : from;
    this.updateState(
      {
        from: fromValue,
        fromInput: formatDate(fromValue, format, this.context),
        to: date,
        toInput: formatDate(date, format, this.context),
        startPoint: fromValue,
        possibleInitialRange: undefined,
        endPoint: date,
        showCloseIcon: true
      },
      null,
      true,
      () => {
        this.updateValidState();
      }
    ).then(() => {
      blur(this.toRef);
      //Throw onChange Event
      if (!this.props.enableFlexibleDates) {
        if (this.isValid(this.state.from, this.state.to)) {
          this.throwOnChangeEvent();
        }
        if (close) {
          this.popover.closePopover();
        }
      }
    });
  };

  stay = ({ date }) => {
    const { format } = this.props;

    this.updateState(
      {
        from: date,
        fromInput: formatDate(date, format, this.context),
        startPoint: date,
        possibleInitialRange: date,
        showCloseIcon: true
      },
      null,
      true,
      () => {
        this.updateValidState();
      }
    ).then(() => {
      blur(this.toRef);
      //Throw onChange Event
      if (!this.props.enableFlexibleDates) {
        if (this.isValid(this.state.from, this.state.to)) {
          this.throwOnChangeEvent();
        }
      }
    });
  };

  handleChange = (date) => {
    if (!isFutureOrPresentDay(date)) {
      return;
    } else {
      const { departure } = this.props;
      if (
        departure &&
        Number(departure.end) &&
        Number(departure.end) >= 0 &&
        Number(departure.start) &&
        Number(departure.start) >= 0 &&
        !isDayInRange(date, {
          from: dayjs().add(Number(departure.start), 'day').toDate(),
          to: this.getToMonth().toDate()
        })
      ) {
        return;
      }
    }
    const { from, to, machineState } = this.state;

    /** The logic in the next sentences not its optmized for easy understanding
     * All logic to select dates it's managed here*/
    if (machineState === STATES.START) {
      if (!from) {
        this.send('stay', { date });
      } else if (dayjs(date).isBefore(dayjs(from))) {
        this.send('stay', { date });
      } else if (dayjs(date).isAfter(dayjs(from))) {
        this.send('selectTo', { date });
      } else if (dayjs(date).isSame(dayjs(from))) {
        this.send('selectTo', { date });
      }
    } else if (machineState === STATES.INTER) {
      if (dayjs(date).isBefore(dayjs(to))) {
        this.send('selectFrom', { date });
      } else if (dayjs(date).isAfter(dayjs(to))) {
        this.send('selectFrom', { date, clearTo: true });
      } else if (dayjs(date).isSame(dayjs(to))) {
        this.send('selectFrom', { date });
      }
    } else if (machineState === STATES.END) {
      if (!from && !to) {
        this.send('selectFrom', { date, sameDate: true, close: true });
      } else if (!to && (dayjs(date).isAfter(dayjs(from)) || dayjs(date).isSame(dayjs(from)))) {
        this.send('selectTo', { date, close: true });
      } else if (dayjs(date).isAfter(dayjs(from))) {
        this.send('selectTo', { date, close: true });
      } else if (dayjs(date).isBefore(dayjs(from))) {
        this.send('selectFrom', { date });
      }
    }
  };

  onMouseEnter = (date) => {
    const { from, to, startPoint, endPoint, isFirstInputClicked } = this.state;
    if (isFutureOrPresentDay(date) && isDate(from) && (!to || isDate(to))) {
      if (this.focusElement === 'from') {
        if (to && endPoint && isFirstInputClicked && isDayAfterOrSame(date, startPoint)) {
          this.setState({
            possibleInitialRange: date
          });
        } else if (!to || isDayBefore(date, to)) {
          // this.updateState({ from: date });
        } else {
          this.updateState(
            {
              // from: startPoint,
              // to: date,
            },
            'to'
          );
        }
      } else if (this.focusElement === 'to') {
        const toMonth = this.getToMonth();
        if (!toMonth || (toMonth && !dayjs(date).isAfter(toMonth))) {
          if (!from || isDayAfterOrSame(date, from)) {
            // this.updateState({ to: date });
          }
        }
      }
    }
  };

  onMouseLeave = (date) => {
    const { possibleInitialRange } = this.state;

    if (isFutureOrPresentDay(date) && this.focusElement === 'from' && possibleInitialRange) {
      this.setState({
        possibleInitialRange: undefined
      });
    }
  };

  handleClosePopover = () => {
    const { to, from } = this.state;
    if (!to && from) {
      this.update();
      if (!this.props.enableFlexibleDates) {
        this.throwOnChangeEvent();
      }
    }

    const { rightIcon } = this.props;
    this.setState({ currentRightIcon: rightIcon });
  };

  handleOpenPopover = () => {
    const { icon: iconOpen, ...iconOpenSettings } = this.props.rightIconOpen;
    const { ...iconSettings } = this.props.rightIcon;
    if (iconSettings.icon) {
      delete iconSettings.icon;
    }

    if (isEmpty(iconOpenSettings)) {
      this.setState({ currentRightIcon: { icon: iconOpen, ...iconSettings } });
    } else {
      this.setState({ currentRightIcon: { icon: iconOpen, ...iconOpenSettings } });
    }
  };

  getCurrentRightIcon = () => {
    let { currentRightIcon } = this.state;
    if (!currentRightIcon) {
      currentRightIcon = this.props.rightIcon;
    }
    const { icon, ...settings } = currentRightIcon;
    return { icon, ...settings };
  };

  handleInputClick = (value) => {
    const { to, from } = this.state;
    if (value === 'to' && !to && from) {
      this.update();
    }
    this.setState({
      machineState: value === 'from' ? STATES.START : STATES.END,
      isFirstInputClicked: value === 'from'
    });
  };

  appendValue = (source, position, value) => {
    return source.indexOf(value, position) - 1 === position ? value : '';
  };

  isValidDateInFuture = (date) => {
    const toMonth = this.getToMonth();
    return (
      !toMonth ||
      (toMonth &&
        !dayjs(dayjs(date).format('YYYY-MM-DD')).isAfter(dayjs(toMonth.format('YYYY-MM-DD'))))
    );
  };

  handleOnKeyDown = (e) => {
    const whiteList = [
      KEY_BACKSPACE,
      KEY_TAB,
      KEY_ARROW_LEFT,
      KEY_ARROW_UP,
      KEY_ARROW_RIGHT,
      KEY_ARROW_DOWN,
      KEY_PAGE_UP,
      KEY_PAGE_DOWN,
      KEY_ENTER
    ];
    const { format } = this.props;
    const { focusElement } = this;
    const { value } = e.target;
    const regex = new RegExp(this.props.allowedCharacters);
    let date = this.state[focusElement];
    const { from, to } = this.state;

    if (!regex.test(e.key) && !whiteList.includes(e.keyCode)) {
      e.preventDefault();
    }
    if (e.keyCode === KEY_ARROW_DOWN || e.keyCode === KEY_PAGE_DOWN) {
      if (focusElement === 'to' && !to && from) {
        date = dayjs(from).add(1, 'day').toDate();
      }
      const unit = e.keyCode === KEY_ARROW_DOWN ? 'day' : 'month';
      const dateValue = date ? dayjs(date).subtract(1, unit).toDate() : dayjs().toDate();
      this.handleKeyboardChange(dateValue, focusElement);
      e.preventDefault();
    } else if (e.keyCode === KEY_ARROW_UP || e.keyCode === KEY_PAGE_UP) {
      if (focusElement === 'to' && !to && from) {
        date = from;
      }
      const unit = e.keyCode === KEY_ARROW_UP ? 'day' : 'month';
      const dateValue = date ? dayjs(date).add(1, unit).toDate() : dayjs().toDate();
      if (this.isValidDateInFuture(dateValue)) {
        this.handleKeyboardChange(dateValue, focusElement);
      }
      e.preventDefault();
    } else if (e.keyCode === KEY_ENTER) {
      this.processDate();
    } else if (e.keyCode !== KEY_BACKSPACE) {
      e.target.value += this.appendValue(format, value.length - 1, this.separator);
    }
  };

  processDate = () => {
    const { fromInput, toInput } = this.state;
    const { format, onInputChange, focusClass } = this.props;
    let typeError;
    if (fromInput === undefined && toInput === undefined) {
      typeError = INCORRECT_FORMAT;
      const newState = {
        fromInput: undefined,
        from: undefined,
        typeError: typeError,
        valid: false,
        showAccessibiltyMessage: true
      };
      const mergedState = { ...this.state, ...newState };
      this.container.classList.remove(focusClass || 'fr-focus:focus');
      this.setState(mergedState);
      this.update();
      return;
    }
    if (fromInput !== undefined && (toInput === undefined || toInput === '')) {
      const value = fromInput;
      dayjs.extend(customParseFormat);
      const date1 = dayjs(value).toDate();
      const date2 = dayjs(value, format).toDate();
      let date = date1;
      if (date2.toString() !== 'Invalid Date') {
        if (
          date1.getFullYear() !== date2.getFullYear() ||
          date1.getMonth() !== date2.getMonth() ||
          date1.getDay() !== date2.getDay()
        ) {
          date = date2;
        }
      }
      const isCorrectFormat = formatDate(date, format, this.context) === value;
      const isFutureOrPresent = isFutureOrPresentDay(date);
      if (!isCorrectFormat) {
        typeError = INCORRECT_FORMAT;
      } else if (this.props.departure !== null) {
        const endDateRange = dayjs().add(
          this.props.departure.end + this.props.departure.start - 1,
          'day'
        );
        const starDateRange = dayjs().add(this.props.departure.start, 'day');
        if (dayjs(date).isBefore(starDateRange) || dayjs(date).isAfter(endDateRange)) {
          typeError = DATE_OUT_OF_RANGE;
        }
      } else if (this.props.availableDays !== null) {
        const endDateRange = dayjs().add(this.props.availableDays, 'day');
        if (dayjs(date).isAfter(endDateRange) || dayjs(date).isSame(endDateRange, 'date')) {
          typeError = DATE_OUT_OF_RANGE;
        }
      }
      if (
        !!date &&
        typeError !== DATE_OUT_OF_RANGE &&
        typeError !== INCORRECT_FORMAT &&
        isFutureOrPresent
      ) {
        const newState = {
          from: date,
          valid: true,
          showAccessibiltyMessage: false,
          showCloseIcon: true
        };
        this.setState(newState, () => {
          clearTimeout(this.deBounce);
          this.deBounce = setTimeout(() => {
            if (newState.valid) {
              const mergedState = { ...this.state, ...newState };
              this.throwOnChangeEvent({
                from: mergedState.from,
                to: mergedState.to,
                flexibleDatesValues: mergedState.flexibleDates
              });
              this.update();
            }
          }, 500);
        });
        onInputChange(value, 'from');
      } else {
        const newState = {
          fromInput: undefined,
          from: undefined,
          typeError: typeError,
          valid: false,
          showAccessibiltyMessage: true
        };
        const mergedState = { ...this.state, ...newState };
        this.container.classList.remove(focusClass || 'fr-focus:focus');
        this.setState(mergedState);
        this.update();
      }
    } else if ((fromInput === undefined || fromInput === '') && toInput !== undefined) {
      const value = toInput;
      dayjs.extend(customParseFormat);
      const date1 = dayjs(value).toDate();
      const date2 = dayjs(value, format).toDate();
      let date = date1;
      if (date2.toString() !== 'Invalid Date') {
        if (
          date1.getFullYear() !== date2.getFullYear() ||
          date1.getMonth() !== date2.getMonth() ||
          date1.getDay() !== date2.getDay()
        ) {
          date = date2;
        }
      }
      const isCorrectFormat = formatDate(date, format, this.context) === value;
      const isFutureOrPresent = isFutureOrPresentDay(date);
      let typeError;
      if (!isCorrectFormat) {
        typeError = INCORRECT_FORMAT;
      } else if (this.props.departure !== null) {
        const endDateRange = dayjs().add(
          this.props.departure.end + this.props.departure.start - 1,
          'day'
        );
        const starDateRange = dayjs().add(this.props.departure.start, 'day');
        if (dayjs(date).isBefore(starDateRange) || dayjs(date).isAfter(endDateRange)) {
          typeError = DATE_OUT_OF_RANGE;
        }
      } else if (this.props.availableDays !== null) {
        const endDateRange = dayjs().add(this.props.availableDays, 'day');
        if (dayjs(date).isAfter(endDateRange) || dayjs(date).isSame(endDateRange, 'date')) {
          typeError = DATE_OUT_OF_RANGE;
        }
      }
      if (
        !!date &&
        typeError !== DATE_OUT_OF_RANGE &&
        typeError !== INCORRECT_FORMAT &&
        isFutureOrPresent
      ) {
        const newState = {
          to: date,
          from: date,
          valid: true,
          fromInput: toInput,
          showAccessibiltyMessage: false,
          showCloseIcon: true
        };
        this.setState(newState, () => {
          clearTimeout(this.deBounce);
          this.deBounce = setTimeout(() => {
            if (newState.valid) {
              const mergedState = { ...this.state, ...newState };
              this.throwOnChangeEvent({
                from: mergedState.from,
                to: mergedState.to,
                flexibleDatesValues: mergedState.flexibleDates
              });
              this.update();
            }
          }, 500);
        });
        onInputChange(value, 'to');
      } else {
        const newState = {
          toInput: undefined,
          to: undefined,
          valid: false,
          typeError: typeError,
          showAccessibiltyMessage: true
        };
        const mergedState = { ...this.state, ...newState };
        this.container.classList.remove(focusClass || 'fr-focus:focus');
        this.setState(mergedState);
        this.update();
      }
    } else if (
      fromInput !== undefined &&
      fromInput !== '' &&
      toInput !== undefined &&
      toInput !== ''
    ) {
      const newState = {
        from: undefined,
        to: undefined,
        valid: undefined,
        typeError: undefined
      };
      const dateFrom = parseDate(fromInput, format);
      const dateTo = parseDate(toInput, format);

      let validDateRange_1 = false;
      let validDateRange_2 = false;
      if (!fromInput || (fromInput && dateFrom)) {
        validDateRange_1 = this.isValid(dateFrom, dateTo);
      }
      if (!toInput || (toInput && dateTo)) {
        validDateRange_2 = this.isValid(dateFrom, dateTo);
      }

      dayjs.extend(customParseFormat);
      const dateFrom1 = dayjs(dateFrom).toDate();
      const dateFrom2 = dayjs(dateFrom, format).toDate();
      let dateA = dateFrom1;
      if (dateFrom2.toString() !== 'Invalid Date') {
        if (
          dateFrom1.getFullYear() !== dateFrom2.getFullYear() ||
          dateFrom1.getMonth() !== dateFrom2.getMonth() ||
          dateFrom1.getDay() !== dateFrom2.getDay()
        ) {
          dateA = dateFrom2;
        }
      }
      const dateTo1 = dayjs(dateTo).toDate();
      const dateTo2 = dayjs(dateTo, format).toDate();
      let dateB = dateTo1;
      if (dateTo2.toString() !== 'Invalid Date') {
        if (
          dateTo1.getFullYear() !== dateTo2.getFullYear() ||
          dateTo1.getMonth() !== dateTo2.getMonth() ||
          dateTo1.getDay() !== dateTo2.getDay()
        ) {
          dateB = dateTo2;
        }
      }
      const isCorrectFormat =
        formatDate(dateA, format, this.context) === fromInput &&
        formatDate(dateB, format, this.context) === toInput;
      let typeError;
      if (!isCorrectFormat) {
        typeError = INCORRECT_FORMAT;
      } else if (
        this.props.departure !== null &&
        dateTo &&
        dateFrom &&
        validDateRange_1 &&
        validDateRange_2
      ) {
        const endDateRange = dayjs().add(
          this.props.departure.end + this.props.departure.start - 1,
          'day'
        );
        const starDateRange = dayjs().add(this.props.departure.start, 'day');
        if (dayjs(dateTo).isBefore(starDateRange) || dayjs(dateTo).isAfter(endDateRange)) {
          typeError = DATE_OUT_OF_RANGE;
        } else if (
          (dayjs(dateFrom).isBefore(starDateRange) &&
            !dayjs(dateFrom).isSame(starDateRange, 'date')) ||
          dayjs(dateFrom).isAfter(endDateRange)
        ) {
          typeError = DATE_OUT_OF_RANGE;
        }
      } else if (
        this.props.availableDays !== null &&
        dateTo &&
        dateFrom &&
        validDateRange_1 &&
        validDateRange_2
      ) {
        const endDateRange = dayjs().add(this.props.availableDays, 'day');
        if (
          dayjs(dateTo).isAfter(endDateRange) ||
          dayjs(dateFrom).isAfter(endDateRange) ||
          dayjs(dateTo).isSame(endDateRange, 'date') ||
          dayjs(dateFrom).isSame(endDateRange, 'date') ||
          dayjs(dateFrom).isBefore(dayjs(new Date()), 'date')
        ) {
          typeError = DATE_OUT_OF_RANGE;
        }
      }
      if (dateTo && dateFrom && validDateRange_1 && validDateRange_2 && typeError === undefined) {
        newState['from'] = dateFrom;
        newState['to'] = dateTo;
        newState['valid'] = true;
      } else {
        newState.valid = false;
      }
      newState.showCloseIcon = !!(
        fromInput.length ||
        toInput.length ||
        newState.from ||
        newState.to ||
        fromInput ||
        toInput
      );
      if (newState.valid === false) {
        if (dateFrom === undefined || dateTo === undefined) {
          newState['typeError'] = INCORRECT_FORMAT;
        } else {
          newState['typeError'] = typeError;
        }
        newState['fromInput'] = '';
        newState['toInput'] = '';
      } else {
        this.container.className += ` ${focusClass || 'fr-focus:focus'}`;
      }
      this.setState(newState, () => {
        clearTimeout(this.deBounce);
        this.deBounce = setTimeout(() => {
          if (newState.valid) {
            const mergedState = { ...this.state, ...newState };
            this.throwOnChangeEvent({
              from: mergedState.from,
              to: mergedState.to,
              flexibleDatesValues: false
            });
            this.update();
          }
        }, 500);
      });
      onInputChange(fromInput, 'from');
      onInputChange(toInput, 'to');
    }
  };

  updateValidState = () => {
    const { fromRef } = this;
    dayjs.extend(customParseFormat);
    const date1 = dayjs(fromRef.current.value).toDate();
    const date2 = dayjs(fromRef.current.value, this.props.format).toDate();
    let date = date1;
    if (date2.toString() !== 'Invalid Date') {
      if (
        date1.getFullYear() !== date2.getFullYear() ||
        date1.getMonth() !== date2.getMonth() ||
        date1.getDay() !== date2.getDay()
      ) {
        date = date2;
      }
    }
    const date11 = dayjs(fromRef.current.value).toDate();
    const date21 = dayjs(fromRef.current.value, this.props.format).toDate();
    let datea = date11;
    if (date21.toString() !== 'Invalid Date') {
      if (
        date11.getFullYear() !== date21.getFullYear() ||
        date11.getMonth() !== date21.getMonth() ||
        date11.getDay() !== date21.getDay()
      ) {
        datea = date21;
      }
    }
    this.setState({
      valid: this.isValid(date, datea)
    });
  };

  onChange = (e, name) => {
    const { value } = e.target;
    const newState = {
      [`${name}Input`]: value
    };
    this.setState(newState);
  };

  setFocusToElement = (element) => {
    if (element === 'from' || element === 'to') {
      this.focusElement = element;
      focus(element === 'from' ? this.fromRef : this.toRef);
    }
  };

  goToNextInput = (value) => {
    this.container.className += ' fr-dates-range-selector--twofields';
    this.setFocusToElement(value);
  };

  componentReference = (el) => {
    this.container = el;
  };

  popoverReference = (el) => {
    this.popover = el;
  };

  focusComponent = (element) => {
    const { onFocus, focusClass } = this.props;
    const { valid } = this.state;
    if (valid !== false) {
      this.container.className += ` ${focusClass || 'fr-focus:focus'}`;
    }
    this.focusElement = element;
    this.isFocused = true;
    onFocus();
  };

  blurComponent = (ev) => {
    ev.persist();
    const { focusClass } = this.props;
    const { showCloseIcon } = this.state;
    this.container.classList.remove(focusClass || 'fr-focus:focus');
    this.isFocused = false;
    setTimeout(() => {
      if (!this.isFocused && this.popover && !showCloseIcon) {
        if (
          !ev.relatedTarget ||
          (this.popoverContainerRef &&
            this.popoverContainerRef.current &&
            !this.popoverContainerRef.current.contains(ev.relatedTarget))
        ) {
          if (ev.relatedTarget.getAttribute('data-testid') !== 'buttonRightIcons') {
            this.popover.closePopover();
          }
        }
      }
    }, 1000);
  };

  clearInputValues = () => {
    this.props.onInputChange('', 'from');
    this.props.onInputChange('', 'to');
    this.setState({
      valid: true,
      fromInput: '',
      toInput: ''
    });
  };

  reset = () => {
    this.setState({ from: undefined, to: undefined, showCloseIcon: false });
    this.clearInputValues();
  };

  clear = (requestFocus = true) => {
    const { onClear } = this.props;
    const { translate } = this.context;
    this.clearInputValues();
    this.setState(
      {
        from: undefined,
        to: undefined,
        showCloseIcon: false,
        startPoint: undefined,
        endPoint: undefined,
        possibleInitialRange: undefined,
        isFirstInputClicked: null,
        machineState: STATES.START
      },
      () => {
        this.container.classList.remove('fr-dates-range-selector--twofields');
        if (this.fromRef && requestFocus) {
          this.focusElement = 'from';
          focus(this.fromRef);
        }
        onClear({ start: undefined, end: undefined });
        if (requestFocus === false) {
          this.setState({
            isCheckedFlexibleDates:
              this.props.enableFlexibleDates && this.props.checkedFlexibleDates,
            flexibleDatesValues: this.props.flexibleDatesRangeValues.defaultValue
          });
        }

        // TODO: For accesibility in imputs
        this.manageAriaLabel(
          translate('cmp-label--datepicker-depart', 'Depart'),
          translate('cmp-label--datepicker-return', 'Return')
        );
      }
    );
  };

  isValid = (start, end) => {
    if (start && end) {
      return isValidRange(start, end);
    }
    return !!start;
  };

  renderInput = ({ component, value, placeholder, ref, onBlur }) => {
    const { format, inputItem, leftInputItem, rightInputItem, showLabel } = this.props;
    const { translate, direction } = this.context;
    const [, style] = getStyles(this.props, { direction });

    const label = component === 'from' ? 'depart' : 'return';
    const settings = { ...inputItem, ...(component === 'from' ? leftInputItem : rightInputItem) };

    return (
      <Input
        id={`fr-${label}-input${this.id}`}
        focusReferences={ref}
        value={value}
        style={style}
        border={false}
        paddingRight="1"
        autoComplete={'off'}
        spellCheck={'off'}
        autoCorrect={'off'}
        ariaAutoComplete={'list'}
        className="fr-flex-2"
        defaultValue={value}
        placeholder={placeholder}
        onFocus={() => this.focusComponent(component)}
        onChange={(e) => this.onChange(e, component)}
        onClick={() => this.handleInputClick(component)}
        onBlur={onBlur}
        onKeyDown={this.handleOnKeyDown}
        leftIcons={['calendar']}
        leftTabIndex={[-1]}
        size="10"
        label={`${translate(`cmp-label--datepicker-${label}`, `cmp-label--datepicker-${label}`)}`}
        ariaDescribedby={`fr-${label}-label-${this.id}`}
        ariaLabelledBy={showLabel ? `input-label-date-range-${this.id}` : null}
        ariaLabelDescription={`${translate(
          'cmp-date-range-selector-instructions',
          'Type the date in this format'
        )} ${translate(
          `date-format-${removeInvalidCharacters(replaceAll(format, ' ', '-'))}`,
          format
        )}`}
        showFocusVisualCue={false}
        {...settings}
      />
    );
  };

  getToMonth = () => {
    const { availableDays, departure } = this.props;
    if (
      departure &&
      Number(departure.end) &&
      Number(departure.end) >= 0 &&
      Number(departure.start) &&
      Number(departure.start) >= 0
    ) {
      return dayjs().add(Number(departure.end) + Number(departure.start), 'day');
    }
    return availableDays && Number(availableDays) && availableDays > 0
      ? dayjs().add(availableDays, 'day')
      : null;
  };

  getOptions = (options) => {
    const { translate } = this.context;
    return options.map((val) => ({
      label: translate(val.name, val.name),
      value: val.value,
      key: val.key
    }));
  };

  handleDoneAction = () => {
    const { from, to } = this.state;
    if (isValidRange(from, to) || !to) {
      this.throwOnChangeEvent();
    }
    this.popover.closePopover();
  };
  renderDayPicker = ({ ref }) => {
    const {
      format,
      modifiersColors,
      renderWeekdayElement,
      dayItems,
      captionItem,
      weekdayItem,
      renderDay,
      showInOneColumn,
      navColor,
      navigationItem,
      renderNavbarElement,
      availableDays,
      numberOfMonths
    } = this.props;
    const { direction, theme } = this.context;
    const [className, style] = getStyles(this.props, { direction, theme });
    const { from, to } = this.state;
    const { modifiers } = this;
    const { translate } = this.context;

    if (ref && ref.current) {
      if (from) {
        ref.current.showMonth(from);
      }
      if (to) {
        ref.current.showMonth(to);
      }
    }

    const footer = this.props.enableFlexibleDates ? (
      <Box {...this.props.footerContainerComponent}>
        <Checkbox
          {...this.props.footerCheckboxComponent}
          ref={this.popupRef}
          className="custom-checkbox"
          isChecked={this.state.isCheckedFlexibleDates}
          tabIndex={0}
          onChange={() => {
            this.handleIsCheckedFlexibleDatesChange(!this.state.isCheckedFlexibleDates);
          }}
          label={translate('cmp-dates-range-label-flexible-dates', 'Flexible dates')}
        />
        <LocationSelectorFilter
          selectMode={true}
          showSelectorMenuArrow={false}
          {...this.props.footerSelectComponent}
          defaultValue={this.state.flexibleDatesValues}
          options={this.getOptions(this.props.flexibleDatesRangeValues.values)}
          dataTestId="locationSelector-flexiblesDates-dateRange"
          placeholder={translate(
            'cmp-location-flexiblesDates-dateRange-placeholder',
            'Select the flexibles dates'
          )}
          labelKey="flexiblesDates"
          onChange={(e) => {
            this.handleFlexibleDatesChange(parseInt(e.value));
          }}
          ariaLabel={translate('cmp-date-selector-aria-description', 'Selector of flexible dates')}
        />
        <div
          style={{
            display: 'flex',
            marginLeft: 'auto'
          }}
        >
          <Button
            onClick={this.clear}
            tabIndex={0}
            disabled={this.state.from === undefined && this.state.to === undefined}
            {...this.props.footerClearButtonComponent}
          >
            {translate('cmp-dates-picker-selector-clear-button', 'Clear')}
          </Button>
          <Button
            onClick={this.handleDoneAction}
            disabled={this.state.from === undefined && this.state.to === undefined}
            {...this.props.footerDoneButtonComponent}
            tabIndex={0}
          >
            {translate('cmp-dates-picker-selector-done-button', 'Done')}
          </Button>
        </div>
      </Box>
    ) : null;

    return (
      <Fragment>
        <DayPicker
          ref={this.dayPickerRef}
          style={style}
          classname={className}
          id={this.id}
          format={format}
          selectedDays={[from, { from, to }]}
          currentMonth={from}
          modifiers={modifiers}
          modifiersColors={modifiersColors}
          weekdayElement={renderWeekdayElement}
          captionItem={captionItem}
          weekdayItem={weekdayItem}
          dayItems={dayItems}
          numberOfMonths={numberOfMonths}
          renderDay={renderDay}
          showInOneColumn={showInOneColumn}
          navColor={navColor}
          navigationItem={navigationItem}
          navbarElement={renderNavbarElement}
          onDayMouseEnter={this.onMouseEnter}
          onDayMouseLeave={this.onMouseLeave}
          onDayClick={this.handleChange}
          onFocus={() => this.goToNextInput(this.focusElement)}
          availableDays={
            this.props.departure && Number(this.props.departure.end)
              ? Number(this.props.departure.start)
                ? Number(this.props.departure.end) + Number(this.props.departure.start)
                : Number(this.props.departure.end)
              : availableDays
          }
          departure={this.props.departure}
        />
        {footer}
      </Fragment>
    );
  };

  render = () => {
    const {
      departInputPlaceholderLabelKey,
      returnInputPlaceholderLabelKey,
      iconClose,
      showLabel,
      labelKey,
      textLabel,
      invalidClass,
      clearButton,
      showSeparator,
      popover,
      onValueClass,
      errorLabel
    } = this.props;

    const { showCloseIcon, valid, toInput, fromInput } = this.state;
    const { translate, direction, theme } = this.context;
    const [className, style] = getStyles(this.props, { direction, theme });
    const { icon: rightIcon, ...rightIconSettings } = this.getCurrentRightIcon();

    const classNameCmp = `fr-dates-range-selector ${
      valid === false ? `${invalidClass || 'fr-input-invalid'}` : ''
    }`;

    let currentOnValueClass = '';
    if (!isNilOrEmpty(onValueClass) && (!isNilOrEmpty(fromInput) || !isNilOrEmpty(toInput))) {
      currentOnValueClass = onValueClass;
    }

    const input = (
      <Box display="block" shadow="none">
        {showLabel && (
          <Text
            display="inline-block"
            textAlign="left"
            width="100"
            id={`input-label-date-range-${this.id}`}
            {...textLabel}
          >
            {translate(labelKey, 'Dates')}
          </Text>
        )}
        <Box
          border
          display="flex"
          className={`${className} ${classNameCmp}`}
          style={{ overflow: 'hidden', ...style }}
          dataTestId="dateRangeSelector"
          bgColor="white"
        >
          <Box
            className={`fr-input fr-input-group fr-w-100 ${currentOnValueClass}`}
            focusReferences={this.componentReference}
          >
            {this.renderInput({
              leftTabIndex: 0,
              component: 'from',
              placeholder: translate(departInputPlaceholderLabelKey, 'Depart'),
              value: fromInput,
              ref: this.fromRef,
              onBlur: (event) => {
                if (
                  this.fromRef.current.contains(event.target) &&
                  !this.toRef.current.contains(event.relatedTarget) &&
                  this.state.from === undefined &&
                  this.state.to === undefined &&
                  this.state.valid !== undefined &&
                  !this.state.valid
                ) {
                  this.setState({ valid: true });
                }
                if (
                  this.state.fromInput === '' &&
                  this.state.from !== undefined &&
                  this.state.fromInput !== undefined
                ) {
                  this.setState({
                    fromInput: this.state.toInput,
                    from: this.state.to
                  });
                  this.throwOnChangeEvent({ newfrom: this.state.to });
                  return;
                }
              }
            })}
            {(showSeparator || (!showSeparator && fromInput && toInput)) && (
              <Box className="DayPicker-separator" />
            )}
            {this.renderInput({
              tabIndex: 0,
              component: 'to',
              placeholder: translate(returnInputPlaceholderLabelKey, 'Return'),
              value: toInput,
              ref: this.toRef,
              onBlur: (event) => {
                if (
                  !this.fromRef.current.contains(event.relatedTarget) &&
                  this.toRef.current.contains(event.target) &&
                  this.state.from === undefined &&
                  this.state.to === undefined &&
                  this.state.valid !== undefined &&
                  !this.state.valid
                ) {
                  this.setState({ valid: true });
                }
              }
            })}
            {showCloseIcon || rightIcon ? (
              <Box className="fr-input-group-append">
                <Button
                  onClick={this.clear}
                  color="transparent"
                  ariaLabel={translate(
                    `cmp-date-range-selector-${showCloseIcon ? 'clear-filter' : 'right-icon'}`,
                    'Clear range'
                  )}
                  paddingWidth="2"
                  dataTestId="ClearButton"
                  {...clearButton}
                  onKeyDown={(event) => {
                    if (!this.isFocused && this.popover && !event.shiftKey && event.keyCode === 9) {
                      this.popover.closePopover();
                    }
                  }}
                  tabIndex={showCloseIcon ? null : -1}
                >
                  <Icon
                    align="middle"
                    icon={showCloseIcon ? iconClose : rightIcon}
                    {...rightIconSettings}
                  />
                </Button>
              </Box>
            ) : null}
          </Box>
        </Box>
        {this.state.valid !== undefined && !this.state.valid && (
          <Text
            display="inline-block"
            textAlign="left"
            width="100"
            id={`error-label-date-range-${this.id}`}
            {...errorLabel}
          >
            {this.state.typeError === INCORRECT_FORMAT
              ? translate(
                  'cmp-date-selector-incorrect-format-error-message',
                  'Incorrect format on the date set'
                )
              : translate('cmp-date-selector-date-out-range-error-message', 'Date out of range')}
          </Text>
        )}
      </Box>
    );
    const dayPicker = this.renderDayPicker({ ref: this.popupRef });

    return (
      <Popover
        containerRef={this.popoverContainerRef}
        containerProps={{
          onBlur: this.blurComponent
        }}
        onClose={this.handleClosePopover}
        onOpen={this.handleOpenPopover}
        element={input}
        focusReferences={this.popoverReference}
        minWidth="unset"
        {...popover}
      >
        {dayPicker}
      </Popover>
    );
  };
}

DatesRangeSelector.propTypes = {
  /**
   * Default settings for both inputs
   */
  inputItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of left input
   */
  leftInputItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of right input
   */
  rightInputItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Setting of input icon when popover is shown
   */
  rightIconOpen: PropTypes.objectOf(PropTypes.any),
  /**
   * Setting of input
   */
  rightIcon: PropTypes.objectOf(PropTypes.any),
  /**
   * Show separator
   */
  showSeparator: PropTypes.bool,
  /**
   * default value of date
   */
  defaultValue: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date)
  }),
  /**
   * day style props
   */
  dayItems: PropTypes.objectOf(PropTypes.any),
  /**
   * Modifiers style
   */
  modifiersColors: PropTypes.objectOf(PropTypes.any),
  /**
   * renderDay function
   */
  renderDay: PropTypes.func,
  /**
   * renderDay function
   */
  renderWeekdayElement: PropTypes.func,
  /**
   * Settings for weekdays
   */
  weekdayItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Color of navigation buttons
   */
  navColor: PropTypes.string,
  /**
   * Settings for navigation
   */
  navigationItem: PropTypes.objectOf(PropTypes.any),
  /**
   * renderNavBarElement
   */
  renderNavbarElement: PropTypes.func,
  /**
   * onClear ranges
   */
  onClear: PropTypes.func,
  /**
   * onChange function
   */
  onChange: PropTypes.func,
  /*
   * onInputChange function
   */
  onInputChange: PropTypes.func,
  /**
   * Patter to format date
   */
  format: PropTypes.string,
  /**
   * Patter to format date
   */
  focusReferences: PropTypes.func,
  /**
   * function fired when focus the component
   */
  onFocus: PropTypes.func,
  /**
   * determine if show the calendar in One or Two Columns
   */
  showInOneColumn: PropTypes.bool,
  /**
   * Placeholder of the departure date
   */
  departInputPlaceholderLabelKey: PropTypes.string,
  /**
   * PLaceholder of the return date
   */
  returnInputPlaceholderLabelKey: PropTypes.string,
  /**
   * Close icon
   */
  iconClose: PropTypes.string,
  /**
   * Label key of input.
   */
  labelKey: PropTypes.string,
  /**
   * Show label of input or not
   */
  showLabel: PropTypes.bool,
  /**
   * Label Settings
   */
  textLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * clear button settings
   */
  clearButton: PropTypes.objectOf(PropTypes.any),
  /**
   * Class when component recive focus
   */
  focusClass: PropTypes.string,
  /**
   * Class when component recive focus
   */
  invalidClass: PropTypes.string,
  /**
   * Date format for the screen readers
   */
  inputDateScreenReader: PropTypes.oneOf([
    'DD/MM/YYYY',
    'MM/DD/YYYY',
    'D MMMM, YYYY',
    'MMMM D, YYYY'
  ]),
  availableDays: PropTypes.number,
  departure: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string
  }),
  captionItem: PropTypes.objectOf(PropTypes.any),
  numberOfMonths: PropTypes.number,
  popover: PropTypes.objectOf(PropTypes.any),
  onValueClass: PropTypes.string,
  allowedCharacters: PropTypes.string,
  enableFlexibleDates: PropTypes.bool,
  checkedFlexibleDates: PropTypes.bool,
  flexibleDatesRangeValues: PropTypes.shape({
    values: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        key: PropTypes.string
      })
    ),
    defaultValue: PropTypes.number
  }),
  footerCheckboxComponent: PropTypes.objectOf(PropTypes.any),
  footerContainerComponent: PropTypes.objectOf(PropTypes.any),
  footerSelectComponent: PropTypes.objectOf(PropTypes.any),
  footerDoneButtonComponent: PropTypes.objectOf(PropTypes.any),
  footerClearButtonComponent: PropTypes.objectOf(PropTypes.any)
};

DatesRangeSelector.defaultProps = {
  allowedCharacters: '[0-9A-Za-z, ]',
  inputItem: {},
  leftInputItem: {},
  rightInputItem: {},
  rightIcon: {},
  rightIconOpen: {},
  defaultValue: {
    start: undefined,
    end: undefined
  },
  showSeparator: true,
  dayItems: {},
  weekdayItem: {
    weekDayFormat: 'oneLetter',
    fontWeight: 'bold',
    border: false
  },
  modifiersColors: {
    beginRange: { bgColor: 'primary', textColor: 'white', padding: '2', roundedLeft: '1' },
    endRange: { bgColor: 'primary', textColor: 'white', padding: '2', roundedRight: '1' },
    selected: { bgColor: 'custom', textColor: 'dark', padding: '2' },
    today: {},
    past: {}
  },
  renderDay: null,
  renderWeekdayElement: null,
  navigationItem: {
    marginWidth: '1'
  },
  navColor: 'link',
  renderNavbarElement: null,
  onClear: () => {},
  onChange: () => {},
  onInputChange: () => {},
  format: 'MM/DD/YYYY',
  focusReferences: () => {},
  onFocus: () => {},
  showInOneColumn: false,
  departInputPlaceholderLabelKey: 'cmp-label--datepicker-depart',
  returnInputPlaceholderLabelKey: 'cmp-label--datepicker-return',
  iconClose: 'close',
  labelKey: 'cmp-date-range-selector-label',
  showLabel: false,
  textLabel: {},
  clearButton: {},
  focusClass: null,
  invalidClass: 'fr-input-invalid',
  inputDateScreenReader: 'MMMM D, YYYY',
  availableDays: null,
  departure: null,
  captionItem: {},
  numberOfMonths: 2,
  popover: null,
  onValueClass: 'fr-value-selected',
  enableFlexibleDates: false,
  checkedFlexibleDates: false,
  flexibleDatesRangeValues: {
    values: [
      { name: 'cmp-flexible-dates-opt1', value: 1, key: 'value1' },
      { name: 'cmp-flexible-dates-opt2', value: 2, key: 'value2' },
      { name: 'cmp-flexible-dates-opt3', value: 3, key: 'value3' },
      { name: 'cmp-flexible-dates-opt4', value: 4, key: 'value4' },
      { name: 'cmp-flexible-dates-opt5', value: 5, key: 'value5' }
    ],
    defaultValue: 4
  },
  footerContainerComponent: {
    style: {
      display: 'flex',
      alignItem: 'center'
    }
  }
};

DatesRangeSelector.contextType = Context;

DatesRangeSelector.componentName = 'DatesRangeSelector';

export default DatesRangeSelector;
